import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { Validator, NG_VALIDATORS, UntypedFormControl } from '@angular/forms';
import Utils from '../../../util';
import { UnitService } from '../../anessa-library/anessa-library/projects/unit-library/src/public-api';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { sanityCheckValues } from './sanity-checking-temp';
import { evaluate } from 'mathjs';

@Directive({
  selector: '[appValidateUnitValue][ngModel]',
  providers: [{
    provide: NG_VALIDATORS,
    useExisting: UnitValueValidatorDirective,
    multi: true
  }]
})
export class UnitValueValidatorDirective implements Validator {
  @Input() appValidateUnitValue = true;
  @Input() unitValue: UnitValue;
  @Input() preventDashValues: boolean;
  @Input() preventNanValues: boolean;
  @Input() preventNegativeValues: boolean;
  @Input() preventPercentValues: boolean;
  @Input() preventNonWholeValues: boolean;
  @Input() greaterThanValue: number = null;
  @Input() lessThanValue: number = null;
  // comes from the name property of the unit-value component, which should be unique for id purposes
  @Input() validationID: string = "";

  @Input() preventZeroValues: boolean;
  @Input() customSuffix: string;

  @Output() errorMessageUpdated = new EventEmitter();
  errorMessage: string;
  warningMessage: string;
  errorParams: any;
  isSanityError: boolean;
  customWarningMessage = "";
  customErrorMessage: string;
  @Input()
  set setCustomErrorMessage(message: string) {
    this.errorMessage = '';
    this.customErrorMessage = message;
    this.errorMessageUpdated.emit();
    if (this.form != null) {
      if (this.validate(this.form)) {
        setTimeout(() => this.form.setErrors({ 'incorrect': true }));
      } else {
        this.form.setErrors(null);
      }
    }
  }

  @Input()
  set setCustomWarningMessage(message: string) {
    this.customWarningMessage = message;
    // reset warningMessage if no external warning
    if (message === "") {
      this.warningMessage = "";
    }
    if (this.form != null) {
      this.validate(this.form)
    }
  }

  @Output() errorMessageUpdate = new EventEmitter<{ message: string, params: any }>();
  @Output() warningMessageUpdate = new EventEmitter<{ message: string }>();
  @Output() validationUpdated = new EventEmitter();

  // These IDs are pulled from the database
  private percentages: string[] = [
    '000000000000000000000101',
    '000000000000000000000201',
    '000000000000000000000301',
    '000000000000000000000401',
    '000000000000000000001502',
    '000000000000000000001801',
    '000000000000000000002101',
    '000000000000000000002102',
    '000000000000000000002201',
    '000000000000000000005401',
    '000000000000000000005501',
    '000000000000000000006301'
  ];

  // percentage like units that need to be increased by a factor of 100 when comparing to value in UI,
  // but do not have a max of 100
  private scaleUpUnits: string[] = [
    '000000000000000000000102',
    '000000000000000000000103',
    '000000000000000000006302',
    '000000000000000000006303'
  ];

  private form: UntypedFormControl;

  constructor(private unitService: UnitService) {}

  validate(form: UntypedFormControl) {
    this.form = form;
    let isValid = true;
    const initialErrorMessage = this.errorMessage;
    if (form.value || form.value === 0) { // input is not empty
      if (this.preventDashValues) {
        // Value changes to scientific notation at e-7 so check all numbers with
        // e and - to ensure they are in proper scientific notation form
        if (form.value.includes('-') && form.value.lastIndexOf('-') > 0 && isNaN(Number(form.value))) {
          isValid = false;
          this.isSanityError = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.NO_DASH';
        } else if (this.errorMessage === 'HELPER.UNIT_VALUE.ERRORS.NO_DASH') {
          this.errorMessage = '';
        }
      }
      if (this.preventNanValues && isNaN(form.value.replace(/[,\-]/g, ''))) {
        isValid = false;
        this.isSanityError = false;
        this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_NUMERIC';
      } else if (this.errorMessage === 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_NUMERIC') {
        this.errorMessage = ''; // it looks like this is to prevent the message from being unset every time it's checked
      }
      if (this.preventNegativeValues) {
        if (Number(form.value.replace(',', '')) < 0 ||
          (form.value.startsWith('-') && !form.value.startsWith('-0'))) {
            this.isSanityError = false;
          isValid = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_NEGATIVE';
        } else if (this.errorMessage === 'HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_NEGATIVE') {
          this.errorMessage = '';
        }
      }
      if (this.preventNonWholeValues) {
        if (form.value.includes('.') || Number(form.value.replace(',', '')) % 1 !== 0) {
          this.isSanityError = false;
          isValid = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_WHOLE_NUMBER';
        } else if (this.errorMessage === 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_WHOLE_NUMBER') {
          this.errorMessage = '';
        }
      }
      // Prevent percentages greater than 100
      if (this.preventPercentValues && this.unitValue) {
        const isGkgWeightProportion = this.unitValue.unit === '000000000000000000000102' || this.unitValue.unit === '000000000000000000006302';
        const islbShortTonWeightProportion = this.unitValue.unit === '000000000000000000000103' || this.unitValue.unit === '000000000000000000006303';
        let maxValue = 100;
        if (isGkgWeightProportion) {
          maxValue = 1000;
        }
        if (islbShortTonWeightProportion) {
          maxValue = 2000;
        }
        let errorMessage: string;
        if (!isGkgWeightProportion && !islbShortTonWeightProportion) {
          errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_GREATER_THAN_PERCENT';
        } else {
          errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_GREATER_THAN';
        }
        if (this.percentages.includes(this.unitValue.unit) || isGkgWeightProportion || islbShortTonWeightProportion) {
          if (form.value.replace(/,/g, '') > maxValue) {
            isValid = false;
            this.isSanityError = false;
            this.errorMessage = errorMessage;
            this.errorParams = { '%d': maxValue };
          } else if (this.errorMessage && this.errorMessage.includes('HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_GREATER_THAN')) {
            this.errorMessage = '';
          }
        } else if (this.errorMessage && this.errorMessage.includes('HELPER.UNIT_VALUE.ERRORS.MUST_NOT_BE_GREATER_THAN')) {
          this.errorMessage = '';
        }
      }
      if (Utils.isNumeric(this.greaterThanValue) && Utils.isNumeric(form.value)) {
        if (form.value <= this.greaterThanValue) {
          isValid = false;
          this.isSanityError = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_GREATER_THAN';
          this.errorParams = { '%d': this.greaterThanValue };
        } else if (this.errorMessage.startsWith('HELPER.UNIT_VALUE.ERRORS.MUST_BE_GREATER_THAN')) {
          this.errorMessage = '';
        }
      }
      if (Utils.isNumeric(this.lessThanValue) && Utils.isNumeric(form.value)) {
        if (form.value >= this.lessThanValue) {
          isValid = false;
          this.isSanityError = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.MUST_BE_LESS_THAN';
          this.errorParams = { '%d': this.lessThanValue };
        } else if (this.errorMessage.startsWith('HELPER.UNIT_VALUE.ERRORS.MUST_BE_LESS_THAN')) {
          this.errorMessage = '';
        }
      }
      // Prevent 0 value
      if (this.preventZeroValues) {
        if (Number(form.value.replace(/,/g, '')) === 0) {
          isValid = false;
          this.isSanityError = false;
          this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.NO_ZERO';
        } else if (this.errorMessage === 'HELPER.UNIT_VALUE.ERRORS.NO_ZERO') {
          this.errorMessage = '';
        }
      }
      this.validateCustomSanityCheck();
      // prioritize the external warning message, which is more specific and from the component
      this.warningMessage = this.customWarningMessage || this.warningMessage;
    } else { // input is empty
      this.errorMessage = '';
      this.warningMessage = '';
    }

    if (this.errorMessage) {
      isValid = false;
    }
    // update unit-value app error message
    if (initialErrorMessage !== this.errorMessage) {
      this.errorMessageUpdate.emit({ message: this.errorMessage, params: this.errorParams });
    } else if (initialErrorMessage === this.errorMessage && this.errorMessage !== '') {
      this.errorMessageUpdate.emit({ message: this.errorMessage, params: this.errorParams });
    }
    if (!this.errorMessage && this.customErrorMessage) {
      isValid = false;
      this.errorMessage = this.customErrorMessage;
      this.errorMessageUpdate.emit({ message: this.errorMessage, params: this.errorParams });
    }
    this.warningMessageUpdate.emit({ message: this.warningMessage });

    this.validationUpdated.emit(true);
    return (isValid) ? null : { invalid: true };
  }

  validateCustomSanityCheck() {
    if ((this.errorMessage === "" || this.isSanityError)) {
      this.errorSanityCheck()
      this.warningSanityCheck()
    }
  }

  private vsTsUnits: string[] = [
    '000000000000000000004210',
    '000000000000000000004211',
    '000000000000000000004212',
    '000000000000000000004213'
  ];

  getValueForComparison(unitValue: UnitValue) {
    let value: number | any = 0;
    if (unitValue.hasValues()) {
      if (this.vsTsUnits.includes(unitValue.unit)) {
        let updatedFactor = this.unitService.getUnitById(unitValue.unit).factor.replace(/VS/g, "" + 1);
        updatedFactor = updatedFactor.replace(/TS/g, "" + 1);
        value = evaluate(updatedFactor.replace(/V/g, String(unitValue.value)));
      } else {
        value = this.unitService.getValueInBaseUnit(unitValue);
      }
      if (typeof value !== 'number') value = value.value;
    }
    // if value is percentage, multiply by 100
    if (this.percentages.includes(this.unitValue.unit) || this.scaleUpUnits.includes(unitValue.unit)) {
      value *= 100;
    }
    return value;
  }

  warningSanityCheck() {
    // check sanity warnings for min / max thresholds
    if (this.validationID in sanityCheckValues.warnings) {
      let value = this.getValueForComparison(this.unitValue)
      const checks = sanityCheckValues.warnings[this.validationID];
      if ('min' in checks && value < checks.min.value) {
        this.warningMessage = 'HELPER.UNIT_VALUE.ERRORS.uncommonly_low';
      } else if ('max' in checks && value > checks.max.value) {
        this.warningMessage = 'HELPER.UNIT_VALUE.ERRORS.uncommonly_high';
      } else {
        this.warningMessage = "";
      }
    }
  }

  errorSanityCheck() {
    let isSanityError = false;
    // check for any sanity errors with min / max thresholds
    if (this.validationID in sanityCheckValues.errors) {
      let value = this.getValueForComparison(this.unitValue)
      const checks = sanityCheckValues.errors[this.validationID];
      if ('min' in checks && (value < checks.min.value)) {
        this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.uncommonly_low';
        isSanityError = true;
      } else if ('max' in checks && (value > checks.max.value)) {
        this.errorMessage = 'HELPER.UNIT_VALUE.ERRORS.uncommonly_high';
        isSanityError = true;
      } else {
        this.errorMessage = "";
      }
    }
    this.isSanityError = isSanityError;
  }
}
